import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../lib/api';
import { toast } from 'sonner';
import { MapPin, RefreshCw } from 'lucide-react';
import { Button } from '../ui/button';

export default function DispatchView({ tickets, technicians }) {
  const navigate = useNavigate();
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [recommendations, setRecommendations] = useState([]);
  const [loadingRecs, setLoadingRecs] = useState(false);

  const openTickets = tickets.filter(t => ['new', 'assigned', 'in_progress'].includes(t.status) && !t.assigned_to);

  const fetchRecommendations = async (ticketId) => {
    setSelectedTicket(ticketId);
    setLoadingRecs(true);
    try {
      const { data } = await api.get(`/api/dispatch/recommend?ticket_id=${ticketId}`);
      setRecommendations(data.recommendations || []);
    } catch { toast.error('Failed to load recommendations'); }
    finally { setLoadingRecs(false); }
  };

  const handleAssign = async (ticketId, techId) => {
    try {
      await api.put(`/api/tickets/${ticketId}`, { assigned_to: techId });
      toast.success('Technician dispatched');
      setRecommendations([]);
      setSelectedTicket(null);
    } catch { toast.error('Dispatch failed'); }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="dispatch-view">
      <div>
        <h1 className="font-heading text-2xl font-bold tracking-tight text-gray-900">Dispatch Center</h1>
        <p className="text-sm text-gray-500 mt-1">AI-powered technician matching by skills, location, and availability</p>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white border border-gray-200 rounded-lg p-5">
          <h2 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-3 flex items-center gap-2">
            <MapPin className="w-4 h-4 text-[#D4A843]" /> Unassigned Tickets ({openTickets.length})
          </h2>
          <div className="space-y-2 max-h-[400px] overflow-y-auto">
            {openTickets.length === 0 && <p className="text-sm text-gray-400 py-4 text-center">All tickets assigned</p>}
            {openTickets.map(t => (
              <button key={t.id} onClick={() => fetchRecommendations(t.id)}
                className={`w-full text-left p-3 rounded-lg border transition-colors ${selectedTicket === t.id ? 'border-[#D4A843] bg-[#D4A843]/5' : 'border-gray-100 hover:bg-gray-50'}`}
                data-testid={`dispatch-ticket-${t.id}`}>
                <p className="text-sm font-medium text-gray-900 truncate">{t.title}</p>
                <div className="flex items-center gap-2 mt-1">
                  <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded badge-${t.priority}`}>{t.priority}</span>
                  <span className="text-[10px] text-gray-400">{t.service_type}</span>
                  <span className="text-[10px] text-gray-400">{t.category}</span>
                </div>
              </button>
            ))}
          </div>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-5">
          <h2 className="text-sm font-bold text-gray-900 uppercase tracking-wider mb-3">Recommended Technicians</h2>
          {loadingRecs ? (
            <div className="flex items-center justify-center py-12"><RefreshCw className="w-5 h-5 animate-spin text-gray-400" /></div>
          ) : recommendations.length === 0 ? (
            <p className="text-sm text-gray-400 py-8 text-center">{selectedTicket ? 'No matching technicians found' : 'Select a ticket to see recommendations'}</p>
          ) : (
            <div className="space-y-2 max-h-[400px] overflow-y-auto">
              {recommendations.map((rec, i) => (
                <div key={rec.technician_id} className="flex items-center justify-between p-3 rounded-lg bg-gray-50" data-testid={`dispatch-rec-${i}`}>
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${i === 0 ? 'bg-[#D4A843] text-[#0A0A0A] font-semibold' : 'bg-gray-200 text-gray-600'}`}>#{i + 1}</div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">{rec.technician_name}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${rec.availability === 'available' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>{rec.availability}</span>
                        {rec.distance_km !== null && <span className="text-[10px] text-gray-400">{rec.distance_km}km</span>}
                        <span className="text-[10px] text-gray-400">${rec.hourly_rate}/hr</span>
                      </div>
                      {rec.skills?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1">
                          {rec.skills.slice(0, 3).map((s, j) => (
                            <span key={`j-${j}`} className="text-[10px] bg-[#D4A843]/10 text-[#D4A843] px-1.5 py-0.5 rounded">{s}</span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-bold text-[#D4A843]">{rec.score}pts</span>
                    <Button size="sm" className="bg-[#D4A843] hover:bg-[#E8C972] text-[#0A0A0A] font-semibold rounded text-xs px-3" onClick={() => handleAssign(selectedTicket, rec.technician_id)} data-testid={`dispatch-assign-${i}`}>
                      Dispatch
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
